import { useContext, useEffect, useRef } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Editor } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { SubtoolVertex } from '@editor/interfaces/tool'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import {
  addInspectionItems,
  removeInspectionItems,
  upsertInspectionItems,
} from '@editor/pages/projects/editor/store/inspectionItems'
import { postInspectionItems } from '@editor/services/InspectionSheet'
import { detectPolylineWidth } from '@editor/services/Polyline'
import { pollAsyncJobStatus } from '@editor/services/VolumeEstimation'
import { GenericError } from '@editor/services/errors/GenericError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems, useVertexSelector } from '@editor/store/selectors'
import { addSavingElementIds, removeSavingElementIds, setDisableTools } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import * as Sentry from '@sentry/react'
import { Vector3 } from 'three'

import { usePolylineSelector } from '../../../store/selector'
import { CrackDetectionStage } from '../../interfaces'
import { setSaving } from '../../store/core'
import { resetCurrent, setDrawingStage } from '../../store/crackDetection'
import {
  clearSavedPolylines,
  copySavingPolylines,
  removeWorkingPolyline,
  resetCompletedPolylines,
  resetEditedPolylines,
  restoreSavingPolylines,
} from '../../store/polyline'
import {
  selectCanSaveAndExitPolylineTool,
  selectCanUndoPolyline,
  selectCrackDetectionStage,
  selectIsPolylineDirty,
  selectIsSaveAndExitMode,
} from '../../store/selectors'
import { addPolylineWidths, deletePolylines, updatePolylines } from '../../store/thunks'

const tempPoint1 = new Vector3()
const tempPoint2 = new Vector3()

const useEditor = (props: Editor): EditorConfig => {
  // Toast
  const successToastIdRef = useRef<string>(undefined)
  const infoToastIdRef = useRef<string>(undefined)

  // Context
  const { selectedTool, changeTool } = props
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionItems = useAppSelector(selectInspectionItems)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const currentVertexSubtool = useVertexSelector((state) => state.currentVertexSubtool)

  const isDirty = usePolylineSelector(selectIsPolylineDirty)
  const canUndo = usePolylineSelector(selectCanUndoPolyline)
  const isSaveable = usePolylineSelector((state) => state.polyline.isSaveable)
  const completedPolylines = usePolylineSelector((state) => state.polyline.completedPolylines)
  const editedPolylines = usePolylineSelector((state) => state.polyline.editedPolylines)
  const hasWorkingPolyline = usePolylineSelector((state) => state.polyline.workingPoints.length > 0)
  const isWorkingPolylineClosable = usePolylineSelector((state) => state.polyline.workingPoints.length >= 2)
  const isSaveExitMode = usePolylineSelector(selectIsSaveAndExitMode)
  const canExitTool = usePolylineSelector(selectCanSaveAndExitPolylineTool)

  const pathEntwine = usePolylineSelector(
    (state) => state.crackDetection.executions[state.crackDetection.areaId]?.pathEntwine,
  )
  const hasChalkResult = usePolylineSelector((state) =>
    Boolean(state.crackDetection.executions[state.crackDetection.areaId]?.hasChalkResult),
  )
  const crackDrawingStage = usePolylineSelector(selectCrackDetectionStage)

  // Flags
  const isToolSelected = selectedTool === EDITOR_TOOLS.POLYLINE

  // Refs
  // Needs to be ref for the check function to get latest value
  const currentAreaIdRef = useRef(inspectionArea?.inspection_area_id)
  const isToolSelectedRef = useRef(isToolSelected)
  const isDirtyRef = useRef(isDirty)
  const crackDrawingStageRef = useRef(crackDrawingStage)

  // Update refs
  useEffect(() => {
    isToolSelectedRef.current = isToolSelected
    currentAreaIdRef.current = inspectionArea?.inspection_area_id
    isDirtyRef.current = isDirty
    crackDrawingStageRef.current = crackDrawingStage
  }, [isToolSelected, inspectionArea, isDirty, crackDrawingStage])

  /**
   * Save polyline to DB
   *
   * @param polyline Measurement to save
   */
  const handleApply = async () => {
    if (
      !project ||
      inspectionArea?.downsample_status?.status !== FileProcessingStatus.SUCCEEDED ||
      !inspectionSheet?.inspection_sheet_id ||
      (!completedPolylines.length && !editedPolylines.length)
    )
      return

    const token = await getAccessToken()
    if (!token) {
      return
    }

    const savingIds = completedPolylines.map(({ id }) => id).concat(editedPolylines.map(({ id }) => id))

    // copy completed polylines over to saving polylines so user can create more polylines while saving
    dispatch(copySavingPolylines())
    dispatch(setSaving(true))
    dispatch(addSavingElementIds(savingIds))

    try {
      // Deal with edited saved polylines first
      let updateHasError = false
      if (editedPolylines.length) {
        // -- Get polylines to UPDATE
        const patchRequestData = editedPolylines
          .filter(({ points, isDeleted }) => !isDeleted && points.length >= 2)
          .map<Partial<InspectionItem> | null>(({ id, points }) => {
            const item = inspectionItems.getById(id)
            if (!item) return null

            return {
              ...item,
              polyline_length: {
                ...item.polyline_length,
                estimated_value: points.reduce((acc, pos, index) => {
                  if (!points[index + 1]) return acc

                  return acc + tempPoint1.fromArray(pos).distanceTo(tempPoint2.fromArray(points[index + 1]))
                }, 0),
                positions_for_distance: points,
              },
            }
          })
          .filter((item): item is Partial<InspectionItem> => !!item)

        // -- Get polyline widths to CREATE or UPDATE
        // If a polyline did not have it before and now has width, we need to create a new width item for it.
        // If a polyline already had width and the width is updated, we need to update the existing width item.
        const widthItemToCreate: Partial<InspectionItem>[] = []
        const widthItemToUpdate: Partial<InspectionItem>[] = []
        editedPolylines
          .filter(({ width, isDeleted }) => !isDeleted && width !== undefined)
          .forEach(({ id, width }) => {
            const item = inspectionItems.getById(id)
            if (!item) return

            const originalWidthItem = inspectionItems.items.find(
              (i) => i.item_type === InspectionItemType.PolylineWidth && i.polyline_length_id === id,
            )
            if (originalWidthItem?.polyline_width?.estimated_value === width) return // No change in width, no need to do anything

            // Sort them into update vs create buckets
            if (originalWidthItem) {
              widthItemToUpdate.push({
                ...originalWidthItem,
                polyline_width: {
                  ...originalWidthItem.polyline_width,
                  estimated_value: width!,
                },
              })
            } else {
              widthItemToCreate.push({
                item_type: InspectionItemType.PolylineWidth,
                polyline_length_id: id,
                polyline_width: {
                  estimated_value: width!,
                },
              })
            }
          })

        // -- Get polylines to DELETE
        // Also include edited items where their width has been removed.
        const deleteIds: string[] = []
        editedPolylines.forEach(({ id, width, isDeleted }) => {
          // Polyline itself was deleted
          if (isDeleted) {
            deleteIds.push(id)
            return
          }

          const originalWidthItem = inspectionItems.items.find(
            (i) => i.item_type === InspectionItemType.PolylineWidth && i.polyline_length_id === id,
          )

          if (
            originalWidthItem &&
            Number.isFinite(originalWidthItem.polyline_width?.estimated_value) &&
            width === undefined
          ) {
            deleteIds.push(originalWidthItem.inspection_item_id!)
          }
        })

        // -- Run update and delete in parallel
        const [updateResult, widthUpdateResult, widthCreateResult, deleteResult] = await Promise.allSettled([
          patchRequestData.length
            ? dispatch(
                updatePolylines({
                  accessToken: token,
                  projectId: project.project_id,
                  items: patchRequestData,
                  showErrorModal,
                }),
              ).unwrap()
            : null,
          widthItemToUpdate.length
            ? dispatch(
                updatePolylines({
                  accessToken: token,
                  projectId: project.project_id,
                  items: widthItemToUpdate,
                  showErrorModal,
                }),
              ).unwrap()
            : null,
          widthItemToCreate.length
            ? dispatch(
                addPolylineWidths({
                  accessToken: token,
                  projectId: project.project_id,
                  inspectionAreaId: inspectionArea.inspection_area_id,
                  inspectionSheetId: inspectionSheet.inspection_sheet_id,
                  items: widthItemToCreate,
                  showErrorModal,
                }),
              ).unwrap()
            : null,
          deleteIds.length
            ? dispatch(
                deletePolylines({
                  accessToken: token,
                  projectId: project.project_id,
                  areaId: inspectionArea.inspection_area_id,
                  sheetId: inspectionSheet.inspection_sheet_id,
                  items: deleteIds,
                }),
              ).unwrap()
            : null,
        ])

        // -- Update store based on results
        // Collect all updated and created inspection items from results to update store in batch
        const allUpdatedItems: InspectionItem[] = []
        if (updateResult.status === 'fulfilled') {
          if (updateResult.value) allUpdatedItems.push(...updateResult.value)
        } else {
          updateHasError = true
        }

        if (widthUpdateResult.status === 'fulfilled') {
          if (widthUpdateResult.value) allUpdatedItems.push(...widthUpdateResult.value)
        } else {
          updateHasError = true
        }

        if (widthCreateResult.status === 'fulfilled') {
          if (widthCreateResult.value) allUpdatedItems.push(...widthCreateResult.value)
        } else {
          updateHasError = true
        }

        if (allUpdatedItems.length) {
          dispatch(upsertInspectionItems(allUpdatedItems))
        }

        // For deleted items, we can directly remove from store as we already have the IDs
        if (deleteResult.status === 'fulfilled') {
          if (deleteResult.value) dispatch(removeInspectionItems(deleteIds))
        } else {
          updateHasError = true
        }

        // -- Cleanup
        dispatch(resetEditedPolylines())
      }

      // Save new completed polylines
      let saveHasError = false
      if (completedPolylines.length) {
        const results = await (async () => {
          const polylinesToSave: Partial<InspectionItem>[] = completedPolylines.map<Partial<InspectionItem>>(
            ({ points, width }) => {
              // Calculate total length of all sections of the polyline
              const totalLength = points.reduce((acc, pos, index) => {
                if (!points[index + 1]) return acc

                return acc + tempPoint1.fromArray(pos).distanceTo(tempPoint2.fromArray(points[index + 1]))
              }, 0)

              return {
                part_name: '', // leave empty for generic name on display until user changes it
                item_type: InspectionItemType.PolylineLength,
                polyline_length: {
                  estimated_value: totalLength,
                  positions_for_distance: points,
                },
                polyline_width: width !== undefined ? { estimated_value: width } : undefined,
              }
            },
          )

          try {
            // -- If there's no crack detection, directly save as inspection items
            if (!pathEntwine || crackDrawingStageRef.current === CrackDetectionStage.SavingPolylines) {
              dispatch(setAttentionText({ message: '' }))

              // Make sure to use `await` to wait for it to complete before progress is reset in `finally` block.
              const result = await postInspectionItems(
                token,
                project.project_id,
                inspectionArea.inspection_area_id,
                inspectionSheet.inspection_sheet_id,
                polylinesToSave,
              )

              // A chalk run has no width-detection job to clean up after it, so its detection state
              // is cleared here — otherwise the drawn area and its FineTune stage outlive the save
              // and the tool stays dirty.
              if (hasChalkResult) {
                dispatch(resetCurrent())
                document.dispatchEvent(new Event('editor:tool:crackDetection:saved'))
              }

              return result
            }

            // -- If there's crack detection, we need to send it for width detection processing
            // At this point, user is not allowed to change tools as we need the job-token first.

            // Lock while we make the background job request
            dispatch(setDisableTools(true))

            const { job_token: jobToken } = await detectPolylineWidth(
              token,
              project.project_id,
              inspectionArea.inspection_area_id,
              inspectionSheet.inspection_sheet_id,
              polylinesToSave,
              pathEntwine,
            )

            if (!jobToken) throw new Error('detect polyline job request failed')

            // Some notification to let user know it's being processed in the background
            if (infoToastIdRef.current) toaster.dismiss(String(infoToastIdRef.current))
            infoToastIdRef.current = toaster.create({
              title: '延長を保存しています。',
              description: '保存が完了すると通知されます。それまでの間、ほかのツールを使用して作業を続けられます。',
              type: 'info',
              duration: 5000,
              closable: true,
            })

            dispatch(setDisableTools(false))
            dispatch(setAttentionText({ message: '' }))
            dispatch(setDrawingStage(CrackDetectionStage.SavingPolylines))

            const response = await pollAsyncJobStatus<InspectionItem[]>(token, jobToken)
            if (!response) throw new Error('saving polylines failed')

            dispatch(resetCurrent())
            document.dispatchEvent(new Event('editor:tool:crackDetection:saved'))

            return response as unknown as InspectionItem[]
          } finally {
            dispatch(setDisableTools(false))
          }
        })()

        if (results) {
          // update inspection items
          dispatch(clearSavedPolylines(savingIds))
          dispatch(addInspectionItems(results))
        } else {
          // Failed save for some reason
          saveHasError = true
        }
      }

      // Throw error if both update and save have errors
      if (updateHasError && saveHasError) {
        throw new Error('Update/save has failed')
      }

      if (successToastIdRef.current) toaster.dismiss(String(successToastIdRef.current))
      if (infoToastIdRef.current) toaster.dismiss(String(infoToastIdRef.current))
      successToastIdRef.current = toaster.create({
        title: '延長を保存しました',
        type: 'success',
        duration: 3000,
        closable: true,
      })
    } catch (error) {
      Sentry.captureException(error)
      changeTool(EDITOR_TOOLS.POLYLINE, true)
      dispatch(restoreSavingPolylines())
      dispatch(setDrawingStage(CrackDetectionStage.Failed))
      showErrorModal(
        error instanceof GenericError ? error.message : '延長計算の保存に失敗しました。再試行してください。',
      )
    } finally {
      dispatch(setSaving(false))
      dispatch(removeSavingElementIds(savingIds))
    }
  }

  /**
   * Handle submit action.
   * It's basically apply + switch to MOVE/SELECT tool.
   */
  const handleSubmit = async () => {
    // Switch to MOVE tool
    changeTool(EDITOR_TOOLS.MOVE, !hasWorkingPolyline)

    // Save if needed
    if (isDirty) {
      if (infoToastIdRef.current) toaster.dismiss(String(infoToastIdRef.current))
      infoToastIdRef.current = toaster.create({
        title: '延長計算を開始しました',
        description:
          '延長計算が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
        type: 'info',
        duration: 5000,
        closable: true,
      })
      await handleApply()
    }
  }

  /**
   * Reset tool state
   */
  const handleReset = () => {
    dispatch(removeWorkingPolyline())
    dispatch(resetCompletedPolylines())
    dispatch(resetEditedPolylines())
    dispatch(resetCurrent())
  }

  return {
    buttons: {
      apply: {
        key: 'apply-polyline',
        onClick: handleApply,
        isLoading: () => false,
        isShown: () => isToolSelected,
        isDisabled: () => !isDirty || !isSaveable,
      },
      submit: {
        key: 'submit-polyline',
        label: isSaveExitMode ? '保存して終了' : undefined,
        onClick: handleSubmit,
        isLoading: () => false,
        isShown: () => isToolSelected,
        isDisabled: () => (isDirty && !isSaveable) || !canExitTool,
      },
      complete: {
        tooltip:
          hasWorkingPolyline && !isWorkingPolylineClosable
            ? `この延長計算の作成を完了するには、あと1点以上が必要です`
            : undefined,
        isShown: () => isToolSelected && currentVertexSubtool === SubtoolVertex.AddVertex,
        isDisabled: () => !isWorkingPolylineClosable,
      },
      undo: {
        isShown: () => isToolSelected && currentVertexSubtool === SubtoolVertex.AddVertex,
        isDisabled: () => !canUndo,
      },
      cancel: {
        isShown: () => isToolSelected && currentVertexSubtool === SubtoolVertex.AddVertex,
        isDisabled: () => !hasWorkingPolyline,
      },
      reset: {
        onClick: handleReset,
        isShown: () => isToolSelected,
        isDisabled: () => !isDirty,
      },
    },
  }
}

export default useEditor
